<template>
  <Modal v-if="modalVisible">
    <template #title>
      {{ $t("pages.badReport.meta.title") }}
    </template>
    <div
      v-if="!isHidden"
      class="flex flex-col px-6"
      style="color:red"
    >
      <p> {{ $t("pages.problem.message") }}</p>
      <ul>
        <li
          v-for="(problem, i) in listProblemsFromApi"
          :key="i"
        >
          - {{ problem.translation }}
        </li>
      </ul>
    </div><br>
    <form
      class="flex flex-col px-6"
      @submit.prevent="submit"
    >
      <div>
        <label
          for="problemsType"
          class="font-bold mr-2"
        >
          {{ $t("pages.problem.type") }}
        </label>
        <span
          class="inline-flex overflow-x-hidden overflow-y-hidden relative"
          :class="{ 'focus-visible': selectInFocus }"
          tabindex="-1"
        >
          <p
            aria-hidden="true"
            class="dropdown pointer-events-none"
            :class="{ 'text-gray-300': problemSelected === ''}"
          >{{ problemSelectedLabel }}</p>
          <select
            id="problemsType"
            ref="problemsType"
            v-model="problemSelected"
            name="problemsType"
            class="absolute cursor-pointer left-0 opacity-0 top-0"
            required
            @focus="parentFocusVisible"
            @blur="selectInFocus = false"
          >
            <option
              value=""
              class="text-gray-300"
            >{{ $t("pages.problem.selectAType") }}</option>
            <option
              v-for="problem in problemsTypeAvailablesSorted"
              :key="problem.translation"
              :value="problem.id"
            >{{ problem.translation }}</option>
          </select>
        </span>
      </div>

      <label
        for="reasonDescription"
        class="sr-only"
      >{{ $t("pages.problem.indicateAReason") }}</label>
      <textarea
        v-if="problemSelected !== ''"
        id="reasonDescription"
        v-model="problemDescription"
        name="reasonDescription"
        class="mt-4"
        :required="problemSelected === 1"
      />

      <footer class="flex flex-wrap justify-end py-6">
        <button
          type="reset"
          class="btn btn__secondary"
          @click="hideModal"
        >
          {{ $t('generic.cancel') }}
        </button>
        <button
          v-tooltip.top="!isLoggedIn && { content: $t('pages.problem.loginReport'), show: true }"
          type="submit"
          class="btn btn__fitMode"
          :disabled="!isLoggedIn || problemSelected === ''"
        >
          {{ $t('generic.confirm') }}
        </button>
      </footer>
    </form>
  </Modal>
</template>

<script>
import { getProblemsExist, getProblemsType, submitProblem } from '@/api/problems';
import { mapGetters } from 'vuex';
import { useToast } from 'vue-toastification';
import Modal from '@/layouts/LayoutModal';

export default {
  name: 'ModalReportProblem',

  components: {
    Modal
  },

  props: {
    modalVisible: {
      type: Boolean,
      default: () => false
    }
  },

  emits: ['update:modalVisible'],

  setup() {
    const toast = useToast();
    return { toast };
  },

  data() {
    return {
      loading: false,
      image_id: '',
      listProblemsFromApi: [],
      isHidden: true,
      problemsType: [],
      problemSelected: '',
      problemDescription: '',
      source: 'image',
      selectInFocus: false
    };
  },

  computed: {

    problemsTypeAvailablesSorted() {
      const typeAvailables = this.problemsType.filter((problem) =>
      {
        const isOtherProblem = problem.id === 1 || problem.id === 6;
        const knownProblems = this.listProblemsFromApi.map(problemKnown => problemKnown.id);
        const isAlreadyKnown = knownProblems.includes(problem.id);
        return isOtherProblem || !isAlreadyKnown;
      }
      );
      return typeAvailables.sort((a, b) => a.id > b.id ? -1 : 1);
    },

    problemSelectedLabel() {
      if (this.problemSelected === '') {
        return this.$t('pages.problem.selectAType');
      } else if (this.problemsType.length > 0) {
        return this.problemsType.find(problem => problem.id === this.problemSelected).translation;
      }
      return '';
    },

    ...mapGetters('user', ['isLoggedIn'])
  },

  watch: {
    '$route': {
      handler() {
        this.image_id = parseInt(this.$route.params.imageId, 10);
        this.isHidden = true;
      },
    },
  },

  created() {
    this.image_id = parseInt(this.$route.params.imageId, 10);

    this.unsubscribeActions = this.$store.subscribeAction({
      after: (action) => {
        if (action.type === 'user/setLanguage') {
          this.loadProblemsType({ source: this.source });
          this.loadProblemsExist({ image_id: this.image_id , state: 'created', source: this.source });
        }
        // Contribute + Geolocalise
        if (action.type === 'explorer/loadImageContribute' || action.type === 'geolocalisation/loadImageToGeoloc' || action.type === 'geolocalisation/loadImageBravo') {
          this.source = 'image';
          this.loadProblemsType({ source: this.source });
          this.loadProblemsExist({ image_id: this.image_id , state: 'created', source: this.source });
        }
        // Visit
        if (action.type === 'explorer/loadImageVisit') {
          this.source = 'geolocalisation';
          this.loadProblemsType({ source: this.source });
          this.loadProblemsExist({ image_id: this.image_id, state: 'created', source: this.source });
        }
      }
    });

  },

  beforeUnmount() {
    if(this.unsubscribeActions()) this.unsubscribeActions();
  },

  methods: {
    loadProblemsExist(params) {
      getProblemsExist(params)
        .then(res => {
          this.listProblemsFromApi = [];
          let problemsExist = [];
          problemsExist = res.data;
          this.loading = false;
          if (problemsExist.length > 0) {
            this.isHidden = false;
            for(let i=0; i < problemsExist.length; i++){
              this.listProblemsFromApi.push(problemsExist[i].problems_type);
            }
          }
        });
    },

    loadProblemsType(params) {
      getProblemsType(params)
        .then(res => {
          this.problemsType = res.data;
          this.loading = false;
        });
    },

    submit() {
      this.loading = true;

      submitProblem({
        image_id: this.image_id,
        problem_type_id: this.problemSelected,
        ...(this.problemDescription !== '' && { description: this.problemDescription })
      })
        .then(res => {
          this.loading = false;
          this.problemDescription = '';
          this.toast.success(res.data.message);
          this.hideModal();
        });
    },

    hideModal() {
      this.problemDescription = '';
      this.problemSelected = '';
      this.$emit('update:modalVisible', false);
    },

    parentFocusVisible(e) {
      if (e.target.classList.contains('focus-visible')) {
        this.selectInFocus = true;
      }
    }
  }
};
</script>
